/**
 * The locale registry.
 *
 * Deliberately free of Vue and browser APIs: the Vite build plugin imports this at
 * build time to emit one HTML page per language, and the runtime imports it too.
 * Keep it that way — adding an import from 'vue' here breaks the build plugin.
 */

export interface LocaleDescriptor {
  readonly code: string
  /** First path segment for this locale, lowercase. Empty for the default locale. */
  readonly slug: string
  /** Name written in its own language — never translated. */
  readonly nativeName: string
  /** BCP 47 value for `hreflang` and `<html lang>`. */
  readonly hreflang: string
  readonly flag: string
}

export const DEFAULT_LOCALE = 'en'

/**
 * Adding a language means:
 *   1. add `src/i18n/locales/<code>.json` in the shell (copy en.json, translate)
 *   2. add `src/locales/<code>.json` in every tool
 *   3. add an entry here
 * The build then emits its pages, hreflang tags and sitemap entries automatically.
 */
export const LOCALES: readonly LocaleDescriptor[] = [
  { code: 'en', slug: '', nativeName: 'English', hreflang: 'en', flag: '🇺🇸' },
  { code: 'pt-BR', slug: 'pt-br', nativeName: 'Português (Brasil)', hreflang: 'pt-BR', flag: '🇧🇷' },
  { code: 'es', slug: 'es', nativeName: 'Español', hreflang: 'es', flag: '🇪🇸' },
]

export const LOCALE_STORAGE_KEY = 'it-tools:locale'

export function isSupportedLocale(code: string | null | undefined): boolean {
  return !!code && LOCALES.some((locale) => locale.code === code)
}

export function findLocale(code: string): LocaleDescriptor | undefined {
  return LOCALES.find((locale) => locale.code === code)
}

export function findLocaleBySlug(slug: string): LocaleDescriptor | undefined {
  // The default locale has an empty slug and must never match a real path segment.
  if (slug === '') return undefined
  return LOCALES.find((locale) => locale.slug === slug.toLowerCase())
}
